import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  Plus,
  Search,
  Filter,
  Pencil,
  Eye,
  FileText,
  Save,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_BASE = "http://nissi-india.com/api/billPass";

/* helpers */
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const fmtAmt = (v, sym = "₹") =>
  `${sym}${parseFloat(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const today = () => new Date().toISOString().split("T")[0];

const sevenBack = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

const BillPassingListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  /* session from localStorage */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* pagination */
  const [limit] = useState(10);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* filters */
  const [filters, setFilters] = useState({
    fromDate: sevenBack(),
    toDate: today(),
    supplier: "",
    srchBranch: "%",
    billPassNo: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: sevenBack(),
    toDate: today(),
    supplier: "",
    srchBranch: "%",
    billPassNo: "",
  });

  /* masters */
  const [supplierList, setSupplierList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [currSymbol, setCurrSymbol] = useState("₹");

  /* grid */
  const [data, setData] = useState([]);
  const [grandTotal, setGrandTotal] = useState("0.00");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* selection */
  const [selectedRows, setSelectedRows] = useState([]);

  /* autocomplete */
  const [acSuggestions, setAcSuggestions] = useState([]);
  const [showAc, setShowAc] = useState(false);

  /* ui */
  const [showFilters, setShowFilters] = useState(true);

  const initialLoadDone = useRef(false);
  const [isInitialReady, setIsInitialReady] = useState(false);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_getSuppliers`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });
      setSupplierList(res.data?.data?.stores || []);
    } catch (err) {
      console.error("Supplier fetch error:", err);
      toast.error("Failed to load suppliers");
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_getBranches`, {
        params: {
          branchid: branchId,
          branchtype: branchType,
        },
        headers: commonHeaders,
      });
      setBranchList(res.data?.data || []);
    } catch (err) {
      console.error("Branch fetch error:", err);
      toast.error("Failed to load branches");
    }
  };

  const fetchCurrency = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bp_currency`, {
        headers: commonHeaders,
      });
      setCurrSymbol(res.data?.data?.currencySymbol || "₹");
    } catch (err) {
      console.error("Currency fetch error:", err);
    }
  };

  const fetchAutoComplete = useCallback(
    async (prefix) => {
      if (!prefix?.trim()) {
        setAcSuggestions([]);
        setShowAc(false);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/bp_autoCompleteNo`, {
          params: {
            branchid: branchId,
            prefix,
          },
          headers: commonHeaders,
        });
        setAcSuggestions(res.data?.data || []);
        setShowAc(true);
      } catch (err) {
        console.error("Autocomplete error:", err);
        setAcSuggestions([]);
        setShowAc(false);
      }
    },
    [branchId, commonHeaders]
  );

  const fetchBillPassList = async (
    searchFilters = appliedFilters,
    searchPage = page
  ) => {
    setLoading(true);
    try {
      const params = {
        branchid: branchId,
        supplier: searchFilters.supplier || "%",
        dtfrom: searchFilters.fromDate || sevenBack(),
        dtto: searchFilters.toDate || today(),
        billpassno: searchFilters.billPassNo || "%",
        srchbranch: searchFilters.srchBranch || "%",
        page: searchPage,
        pageSize: limit,
      };

      const res = await axios.get(`${API_BASE}/bp_billPassList`, {
        params,
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      const backendTotalCount =
        Number(res.data?.totalCount || res.data?.count || rows.length || 0) ||
        0;

      const normalizedRows = rows.map((row) => ({
        ...row,
        _selected: selectedRows.includes(row.fld_billid),
      }));

      setData(normalizedRows);
      setGrandTotal(res.data?.grandTotal || "0.00");
      setTotalCount(backendTotalCount);

      const calculatedPages =
        backendTotalCount > 0 ? Math.ceil(backendTotalCount / limit) : 1;
      setTotalPages(calculatedPages);
    } catch (err) {
      console.error("Bill pass fetch error:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "Failed to fetch bill pass records"
      );
      setData([]);
      setGrandTotal("0.00");
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setSelectedRows([]);
    setPage(1);
    setAppliedFilters(nextFilters);
  };

  const clearFilters = () => {
    const reset = {
      fromDate: sevenBack(),
      toDate: today(),
      supplier: "",
      srchBranch: "%",
      billPassNo: "",
    };
    setFilters(reset);
    setAppliedFilters(reset);
    setSelectedRows([]);
    setPage(1);
  };

  const handleFieldChange = (rowIndex, field, value) => {
    setData((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row))
    );
  };

  const handleRowSelect = (billId, checked) => {
    if (checked) {
      setSelectedRows((prev) => [...new Set([...prev, billId])]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== billId));
    }
    setData((prev) =>
      prev.map((row) =>
        row.fld_billid === billId ? { ...row, _selected: checked } : row
      )
    );
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const ids = data.map((row) => row.fld_billid);
      setSelectedRows(ids);
      setData((prev) => prev.map((row) => ({ ...row, _selected: true })));
    } else {
      setSelectedRows([]);
      setData((prev) => prev.map((row) => ({ ...row, _selected: false })));
    }
  };

  const handleSaveCForm = async () => {
    const selectedData = data.filter((row) =>
      selectedRows.includes(row.fld_billid)
    );

    if (selectedData.length === 0) {
      toast.warning("Please select at least one row");
      return;
    }

    setSaving(true);
    try {
      const updates = selectedData.map((r) => ({
        billid: r.fld_billid,
        cformno: r.fld_cformno || "",
        cformdate: r.fld_cformdate || "",
      }));

      const res = await axios.post(
        `${API_BASE}/bp_updateCForm`,
        { updates },
        { headers: commonHeaders }
      );

      if (res.data?.success || res.status === 200) {
        toast.success("Updated successfully");
        fetchBillPassList(appliedFilters, page);
      } else {
        toast.error("Failed to update C.Form details");
      }
    } catch (err) {
      console.error("CForm update error:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "Failed to update C.Form details"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBillPassClick = (row, mode = "view") => {
    navigate("/inventory/BillPassing_EntryGST", {
      state: {
        billpassno: row.fld_billpassno || row.fld_BillPassNo,
        mode,
      },
    });
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchSuppliers(), fetchBranches(), fetchCurrency()]);
      setIsInitialReady(true);
    };

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadInitialData();
    }
  }, []);

  useEffect(() => {
    if (!isInitialReady) return;
    fetchBillPassList(appliedFilters, page);
  }, [appliedFilters, page, isInitialReady]);

  const pageTotal = data.reduce(
    (sum, row) => sum + parseFloat(row.fld_TotalValue_New || 0),
    0
  );

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Bill Pass Listing</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Bill Pass Listing</h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Search, edit and manage bill pass records
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {totalCount} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""
                }`}
              >
                {showFilters ? <X size={16} /> : <Filter size={16} />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate("/inventory/BillPassing_Add")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                {/* Bill Pass No with autocomplete */}
                <div className="relative">
                  <label className={labelClass}>Bill Pass No</label>
                  <input
                    type="text"
                    placeholder="Search bill pass no..."
                    className={inputClass}
                    value={filters.billPassNo}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters((prev) => ({ ...prev, billPassNo: value }));
                      fetchAutoComplete(value);
                    }}
                    onBlur={() => setTimeout(() => setShowAc(false), 200)}
                  />
                  {showAc && acSuggestions.length > 0 && (
                    <ul
                      className={`absolute z-30 w-full mt-1 rounded-xl shadow-xl border text-sm max-h-44 overflow-auto ${
                        isDark
                          ? "bg-slate-800 border-slate-600"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {acSuggestions.map((s, i) => (
                        <li
                          key={i}
                          onMouseDown={() => {
                            setFilters((prev) => ({
                              ...prev,
                              billPassNo: s,
                            }));
                            setShowAc(false);
                          }}
                          className={`px-4 py-2 cursor-pointer ${
                            isDark ? "hover:bg-slate-700" : "hover:bg-blue-50"
                          }`}
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    className={inputClass}
                    value={filters.supplier}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        supplier: e.target.value,
                      }))
                    }
                  >
                    <option value="">All Suppliers</option>
                    {supplierList.map((s) => (
                      <option key={s.fld_supplierid} value={s.fld_supplierid}>
                        {s.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    className={inputClass}
                    value={filters.srchBranch}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        srchBranch: e.target.value,
                      }))
                    }
                  >
                    <option value="%">All Branches</option>
                    {branchList.map((b) => (
                      <option
                        key={b.fld_branchid || b.fld_Branchid || b.value}
                        value={b.fld_branchid || b.fld_Branchid || b.value}
                      >
                        {b.fld_branchname || b.fld_BranchName || b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={filters.fromDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={filters.toDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={clearFilters}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  {loading ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-xl font-bold">Bill Pass Records</h3>
              </div>

              <button
                onClick={handleSaveCForm}
                disabled={selectedRows.length === 0 || saving}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl shadow transition ${
                  selectedRows.length > 0 && !saving
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-gray-400 text-white cursor-not-allowed"
                }`}
              >
                {saving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {saving
                  ? "Saving..."
                  : `Save C.Form (${selectedRows.length})`}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    <input
                      type="checkbox"
                      checked={
                        data.length > 0 && selectedRows.length === data.length
                      }
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Bill Pass No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Branch
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Ref No (GRN No)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Supplier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Invoice No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    C.Form No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    C.Form Date
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr
                      key={i}
                      className={isDark ? "bg-gray-800/20" : "bg-white"}
                    >
                      {[...Array(12)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton
                            height={20}
                            baseColor={isDark ? "#374151" : "#f3f4f6"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <FileText size={48} className="text-gray-400" />
                        <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          No bill pass records found
                        </div>
                        <div className="text-sm text-gray-500">
                          Try adjusting your filters and search again
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => {
                    const isSelected = selectedRows.includes(row.fld_billid);

                    return (
                      <tr
                        key={row.fld_billid || i}
                        className={`transition ${
                          isSelected
                            ? isDark
                              ? "bg-blue-900/30"
                              : "bg-blue-50"
                            : isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) =>
                              handleRowSelect(row.fld_billid, e.target.checked)
                            }
                            className="w-4 h-4 rounded"
                          />
                        </td>

                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * pageSize + i + 1}
                        </td>

                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleBillPassClick(row, "view")}
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            } hover:opacity-80`}
                          >
                            {fmt(row.fld_billpassno)}
                          </button>
                        </td>

                        <td className="px-4 py-3">{fmt(row.fld_branchname)}</td>
                        <td className="px-4 py-3">{fmt(row.fld_GRNno)}</td>
                        <td className="px-4 py-3 font-medium">
                          {fmt(row.fld_suppliername)}
                        </td>
                        <td className="px-4 py-3">{fmt(row.fld_invno)}</td>
                        <td className="px-4 py-3">
                          {row.fld_EntryDate
                            ? new Date(row.fld_EntryDate).toLocaleDateString(
                                "en-GB"
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-600">
                          {fmtAmt(row.fld_TotalValue_New, currSymbol)}
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={row.fld_cformno || ""}
                            onChange={(e) =>
                              handleFieldChange(i, "fld_cformno", e.target.value)
                            }
                            className={`${inputClass} w-36`}
                            placeholder="C.Form No"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="date"
                            value={
                              row.fld_cformdate
                                ? row.fld_cformdate.split("T")[0]
                                : ""
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                i,
                                "fld_cformdate",
                                e.target.value
                              )
                            }
                            className={`${inputClass} w-40`}
                          />
                        </td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* <button
                              onClick={() => handleBillPassClick(row, "view")}
                              className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                              title="View"
                            >
                              <Eye size={16} />
                            </button> */}
                            <button
                              onClick={() => handleBillPassClick(row, "edit")}
                              className="p-1.5 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition"
                              title="View"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {!loading && data.length > 0 && (
                <tfoot>
                  <tr
                    className={`${
                      isDark ? "bg-gray-800/40" : "bg-gray-50"
                    } font-bold`}
                  >
                    <td colSpan={8} className="px-4 py-3 text-right text-sm">
                      Page Total:
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-green-600">
                      {fmtAmt(pageTotal, currSymbol)}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Footer / Pagination / Stats */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              className={`text-sm ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Showing{" "}
              <span className="font-bold">
                {data.length ? (page - 1) * limit + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-bold">
                {Math.min(page * limit, totalCount)}
              </span>{" "}
              of <span className="font-bold">{totalCount}</span> results
            </p>

            {loading ? (
              <div className="flex space-x-2">
                <Skeleton width={80} height={40} borderRadius={12} />
                <Skeleton width={40} height={40} borderRadius={12} />
                <Skeleton width={40} height={40} borderRadius={12} />
                <Skeleton width={80} height={40} borderRadius={12} />
              </div>
            ) : totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    isDark
                      ? "border-slate-600 hover:bg-slate-800"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronLeft size={16} />
                </button>

                {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                  const start = Math.max(
                    1,
                    Math.min(page - 2, totalPages - 4)
                  );
                  const pageNumber = start + idx;
                  if (pageNumber > totalPages) return null;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                        page === pageNumber
                          ? `${buttonClass} text-white shadow`
                          : isDark
                          ? "border border-slate-600 hover:bg-slate-800 text-gray-300"
                          : "border border-gray-300 hover:bg-gray-50 text-gray-600"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                {totalPages > 5 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                        page === totalPages
                          ? `${buttonClass} text-white shadow`
                          : isDark
                          ? "border border-slate-600 hover:bg-slate-800 text-gray-300"
                          : "border border-gray-300 hover:bg-gray-50 text-gray-600"
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page === totalPages}
                  className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    isDark
                      ? "border-slate-600 hover:bg-slate-800"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            ) : null}
          </div>

          {/* Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-center">
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-blue-600">
                  {totalCount}
                </div>
                <div className="text-xs text-gray-500">Total Records</div>
              </div>
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-green-600">
                  {selectedRows.length}
                </div>
                <div className="text-xs text-gray-500">Selected</div>
              </div>
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-purple-600">
                  {fmtAmt(grandTotal, currSymbol)}
                </div>
                <div className="text-xs text-gray-500">Grand Total</div>
              </div>
              <div
                className={`p-3 rounded-xl ${
                  isDark ? "bg-gray-800/30" : "bg-gray-50"
                }`}
              >
                <div className="text-2xl font-bold text-orange-600">{page}</div>
                <div className="text-xs text-gray-500">Current Page</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme={isDark ? "dark" : "light"}
        toastStyle={{
          borderRadius: "12px",
          fontSize: "14px",
        }}
      />
    </LayoutWrapper>
  );
};

export default BillPassingListing;